import React, { useEffect, useState } from 'react';
import { Table, Spin, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  import_total: number;
  export_total: number;
}

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  import_total: number;
  export_total: number;
  departments?: DepartmentData[];
}

interface GrandTotal {
  import_total: number;
  export_total: number;
}

interface TonnageReportProps {
  reportKey: string;
  filters: any;
}

const TonnageReport: React.FC<TonnageReportProps> = ({ reportKey, filters }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<CategoryData[]>([]);
  const [grandTotal, setGrandTotal] = useState<GrandTotal>({ import_total: 0, export_total: 0 });

  useEffect(() => {
    if (reportKey) fetchReportData();
  }, [reportKey, filters]);

  const fetchReportData = async () => {
    const reportApi = getReportApiByKey(reportKey);
    if (!reportApi) {
      message.error('Invalid report key.');
      return;
    }

    try {
      setLoading(true);
      const response = await reportApi.generateReport(filters);

      setGrandTotal(response?.report?.grand_total || { import_total: 0, export_total: 0 });

      // Transform the nested object structure to array
      const categories: CategoryData[] = Object.entries(response?.report?.categories || {}).map(
        ([categoryId, categoryData]: [string, any]) => {
          const departments = Object.entries(categoryData.departments || {}).map(
            ([deptId, deptData]: [string, any]) => ({
              id: deptId,
              code: deptData.department_code,
              name: deptData.department_name,
              import_total: deptData.import_total || 0,
              export_total: deptData.export_total || 0,
            }),
          );

          // Calculate category totals from departments
          const categoryImportTotal = departments.reduce((sum, dept) => sum + (dept.import_total || 0), 0);
          const categoryExportTotal = departments.reduce((sum, dept) => sum + (dept.export_total || 0), 0);

          return {
            id: categoryId,
            code: categoryData.product_category_code,
            name: categoryData.product_category_name,
            import_total: categoryImportTotal,
            export_total: categoryExportTotal,
            departments: departments,
          };
        },
      );

      setReportData(categories);
    } catch (error) {
      console.error(error);
      message.error('Failed to load report data.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Category / Department',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Import Total',
      dataIndex: 'import_total',
      key: 'import_total',
      align: 'right' as const,
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
    {
      title: 'Export Total',
      dataIndex: 'export_total',
      key: 'export_total',
      align: 'right' as const,
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
  ];

  // Transform data for table display with nested structure
  const transformData = (categories: CategoryData[]) =>
    categories.map((category) => ({
      key: category.id,
      name: `${category.code} - ${category.name}`,
      import_total: category.import_total,
      export_total: category.export_total,
      children: category.departments?.map((dept) => ({
        key: `${category.id}-${dept.id}`,
        name: `↳ ${dept.code} - ${dept.name}`,
        import_total: dept.import_total,
        export_total: dept.export_total,
      })),
    }));

  const tableData = transformData(reportData);

  return (
    <Spin spinning={loading}>
      <Table
        title={() => <div style={{ textAlign: 'center', fontWeight: 700 }}>Tonnage Report</div>}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        expandable={{
          defaultExpandAllRows: true,
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
              <Table.Summary.Cell index={0}>Grand Total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                {grandTotal.import_total.toLocaleString('en-LK')}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                {grandTotal.export_total.toLocaleString('en-LK')}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Spin>
  );
};

export default TonnageReport;
